import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { apiGetQuizLeaderboard } from '../../../api/quizApi';
import type { QuizLeaderboardEntry } from '../../../types/models/quiz';

type Period = 'all' | 'week' | 'month';

const PERIOD_LABELS: Record<Period, string> = { all: 'Sempre', week: 'Settimana', month: 'Mese' };
const RANK_COLORS: Record<number, string> = { 1: 'gold', 2: '#c0c0c0', 3: '#cd7f32' };

// ─── Styled ───────────────────────────────────────────────────────────────────

const Container = styled.div`
  padding: 20px 14px 70px;
  min-height: calc(100vh - 70px);
`;

const PageTitle = styled.h1`
  font-size: 20px;
  font-weight: 900;
  color: #fff;
  margin: 0 0 4px;
`;

const PageSub = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
  margin: 0 0 16px;
`;

const PeriodRow = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 16px;
`;

const PeriodBtn = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 7px 0;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${({ $active }) => ($active ? '#38bdf8' : 'rgba(255,255,255,0.12)')};
  background-color: ${({ $active }) => ($active ? 'rgba(56,189,248,0.12)' : 'transparent')};
  color: ${({ $active }) => ($active ? '#38bdf8' : 'rgba(255,255,255,0.45)')};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background-color: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  cursor: pointer;
  &:active { border-color: rgba(56, 189, 248, 0.3); }
`;

const RankNum = styled.span<{ $rank: number }>`
  font-size: 15px;
  font-weight: 900;
  min-width: 28px;
  color: ${({ $rank }) => RANK_COLORS[$rank] ?? 'rgba(255,255,255,0.3)'};
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const Username = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Nickname = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.35);
`;

const Stats = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

const CorrectCount = styled.span`
  font-size: 14px;
  font-weight: 900;
  color: #fff;
`;

const PctBadge = styled.span<{ $value: number }>`
  font-size: 11px;
  font-weight: 700;
  color: ${({ $value }) => ($value >= 70 ? '#5adf7a' : $value >= 40 ? '#fbbf24' : 'rgba(255,255,255,0.4)')};
`;

const StatusText = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.35);
  margin-top: 32px;
  font-size: 14px;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const QuizLeaderboardMobile: React.FC = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>('all');
  const [entries, setEntries] = useState<QuizLeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    apiGetQuizLeaderboard(period)
      .then(setEntries)
      .catch(() => setEntries([]))
      .finally(() => setIsLoading(false));
  }, [period]);

  return (
    <Container>
      <PageTitle>Classifica Quiz</PageTitle>
      <PageSub>Chi risponde meglio alle domande GWF</PageSub>

      <PeriodRow>
        {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
          <PeriodBtn key={p} $active={period === p} onClick={() => setPeriod(p)}>
            {PERIOD_LABELS[p]}
          </PeriodBtn>
        ))}
      </PeriodRow>

      {isLoading && <StatusText>Caricamento...</StatusText>}

      {!isLoading && entries.length === 0 && (
        <StatusText>Nessun dato disponibile per questo periodo.</StatusText>
      )}

      {!isLoading && entries.length > 0 && (
        <List>
          {entries.map((e, i) => (
            <Row key={e.user_id} onClick={() => navigate(`/carte/utente/${e.username}`)}>
              <RankNum $rank={i + 1}>{i + 1}</RankNum>
              <UserInfo>
                <Username>{e.username}</Username>
                {e.nickname_forum && <Nickname>{e.nickname_forum}</Nickname>}
              </UserInfo>
              <Stats>
                <CorrectCount>{e.correct_answers} / {e.total_answers}</CorrectCount>
                <PctBadge $value={e.percentage}>{e.percentage.toFixed(1)}%</PctBadge>
              </Stats>
            </Row>
          ))}
        </List>
      )}
    </Container>
  );
};

export default QuizLeaderboardMobile;
